import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { Viewer } from "cesium";
import "cesium/Build/Cesium/Widgets/widgets.css";
import "./styles.css";
import { clusterPoints, type ClusteredPoint, type DistanceUnit, type InputPoint, type Linkage } from "./clustering";
import { demoData, parseFile } from "./data";
import type { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";

const palette = ["#ff6542", "#4e8cff", "#13b88a", "#a66cff", "#f2b134", "#ed4f91", "#1aa7b8", "#778f38"];
const byId = <T = HTMLElement>(id: string): T => {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing required element: ${id}`);
  return element as T;
};

const fileInput = byId<HTMLInputElement>("file-input");
const dropzone = byId<HTMLLabelElement>("dropzone");
const thresholdInput = byId<HTMLInputElement>("threshold");
const slider = byId<HTMLInputElement>("threshold-slider");
const units = byId<HTMLSelectElement>("units");
const clusterButton = byId<HTMLButtonElement>("cluster-button");
const status = byId<HTMLParagraphElement>("status");
const results = byId<HTMLElement>("results");
const emptyState = byId<HTMLElement>("empty-state");
const hullToggle = byId<HTMLInputElement>("show-hulls");
const googleDialog = byId<HTMLDialogElement>("google-dialog");
const cesiumDialog = byId<HTMLDialogElement>("cesium-dialog");
const layerDialog = byId<HTMLDialogElement>("layer-dialog");
const layerOpacity = byId<HTMLInputElement>("layer-opacity");
const layerStatus = byId<HTMLElement>("layer-status");
const overlayList = byId<HTMLElement>("overlay-list");
const googleApiKey = byId<HTMLInputElement>("google-api-key");
const googleMapId = byId<HTMLInputElement>("google-map-id");
const cesiumToken = byId<HTMLInputElement>("cesium-token");
const cesiumAssets = byId<HTMLInputElement>("cesium-assets");
const cesiumOsmBuildings = byId<HTMLInputElement>("cesium-osm-buildings");

let sourcePoints: InputPoint[] = [];
let clusteredPoints: ClusteredPoint[] = [];
let currentName = "";
let linkage: Linkage = "single";
let mapStyle = "light";
let leafletMap: L.Map | undefined;
let leafletBase: L.TileLayer | undefined;
let pointLayer: L.LayerGroup | undefined;
let hullLayer: L.LayerGroup | undefined;
interface Overlay {
  id: number;
  name: string;
  data: FeatureCollection<Geometry, GeoJsonProperties>;
  color: string;
  field: string; // "" = single colour, otherwise choropleth by this numeric field
  visible: boolean;
  layer?: L.GeoJSON;
}
const layerPalette = ["#2d78c4", "#0c8f6e", "#b8563f", "#7d4fbf", "#c2902a", "#15889b", "#d2557f", "#5a7d2a"];
let overlays: Overlay[] = [];
let overlaySeq = 0;
let overlayOpacity = 0.6;
let googleMap: google.maps.Map | undefined;
let googleOverlayData: google.maps.Data[] = [];
let googleOverlays: Array<google.maps.Circle | google.maps.Polygon> = [];
let googleLoader: Promise<void> | undefined;
let cesiumViewer: Viewer | undefined;

const tileStyles: Record<string, { url: string; attribution: string; maxZoom: number }> = {
  light: { url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", attribution: '&copy; OpenStreetMap &copy; CARTO', maxZoom: 20 },
  dark: { url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", attribution: '&copy; OpenStreetMap &copy; CARTO', maxZoom: 20 },
  satellite: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: "Tiles &copy; Esri", maxZoom: 19 },
};

const linkageCopy: Record<Linkage, { title: string; description: string }> = {
  single: { title: "Single-link", description: "joins clusters when their nearest members are within the threshold. Best for large files." },
  complete: { title: "Complete-link", description: "merges only when the farthest cross-cluster pair is within the threshold. Produces compact groups; limited to 1,200 points." },
  average: { title: "Average-link", description: "uses the size-weighted mean distance between clusters. A balanced exact method; limited to 1,200 points." },
};

const labelFields = ["label", "name", "title", "site", "facility", "address", "description", "location", "id"];
const categoryFields = ["category", "type", "class", "kind", "group", "cluster", "cluster_id", "clusterid", "status", "sector", "source", "syntheticgroup"];

function setStatus(message: string, error = false): void {
  status.textContent = message;
  status.classList.toggle("error", error);
}

function loadPoints(points: InputPoint[], name: string, suggestedThreshold?: number): void {
  sourcePoints = points;
  clusteredPoints = [];
  currentName = name;
  clusterButton.disabled = false;
  if (suggestedThreshold) {
    thresholdInput.value = String(suggestedThreshold);
    if (suggestedThreshold <= Number(slider.max)) slider.value = String(suggestedThreshold);
  }
  setStatus(`${points.length.toLocaleString()} valid points loaded. Choose a linkage method and build clusters.`);
  renderMap(points, false);
}

function destroyLeaflet(): void {
  if (leafletMap) {
    leafletMap.remove();
    leafletMap = undefined;
    leafletBase = undefined;
    pointLayer = undefined;
    hullLayer = undefined;
  }
}

function destroyCesium(): void {
  if (!cesiumViewer) return;
  document.querySelector(".map-stage")?.classList.remove("cesium-active");
  if (cesiumViewer && !cesiumViewer.isDestroyed()) cesiumViewer.destroy();
  cesiumViewer = undefined;
  const mapElement = byId<HTMLElement>("map");
  mapElement.replaceChildren();
  mapElement.removeAttribute("style");
}

function clearGoogleOverlays(): void {
  googleOverlays.forEach((overlay) => overlay.setMap(null));
  googleOverlays = [];
  googleOverlayData.forEach((data) => data.setMap(null));
  googleOverlayData = [];
}

function ensureLeaflet(): L.Map {
  googleMap = undefined;
  clearGoogleOverlays();
  destroyCesium();
  if (!leafletMap) leafletMap = L.map("map", { zoomControl: true, preferCanvas: true, fadeAnimation: true });
  const activeMap = leafletMap;
  leafletBase?.remove();
  const style = tileStyles[mapStyle] ?? tileStyles.light;
  leafletBase = L.tileLayer(style.url, { attribution: style.attribution, maxZoom: style.maxZoom }).addTo(activeMap);
  pointLayer?.remove();
  hullLayer?.remove();
  overlays.forEach((overlay) => { overlay.layer?.remove(); overlay.layer = undefined; });
  hullLayer = L.layerGroup().addTo(leafletMap);
  pointLayer = L.layerGroup().addTo(leafletMap);
  return activeMap;
}

function referenceColor(value: number, minimum: number, maximum: number): string {
  const ratio = maximum === minimum ? 0.5 : Math.max(0, Math.min(1, (value - minimum) / (maximum - minimum)));
  const colors = ["#2d78c4", "#58a7b8", "#f1cc62", "#ef8b4a", "#c63d4f"];
  return colors[Math.min(colors.length - 1, Math.floor(ratio * colors.length))];
}

function overlayRange(overlay: Overlay): { minimum: number; maximum: number } {
  const values = overlay.data.features.map((feature) => Number(feature.properties?.[overlay.field])).filter(Number.isFinite);
  return { minimum: values.length ? Math.min(...values) : 0, maximum: values.length ? Math.max(...values) : 1 };
}

function referenceFields(data: FeatureCollection<Geometry, GeoJsonProperties>): string[] {
  const fields = new Set<string>();
  data.features.slice(0, 500).forEach((feature) => Object.entries(feature.properties ?? {}).forEach(([key, value]) => { if (Number.isFinite(Number(value))) fields.add(key); }));
  return [...fields];
}

function colorFor(overlay: Overlay, value: number, range: { minimum: number; maximum: number }): string {
  return overlay.field && Number.isFinite(value) ? referenceColor(value, range.minimum, range.maximum) : overlay.color;
}

function rerenderMap(): void {
  if (sourcePoints.length) renderMap(clusteredPoints.length ? clusteredPoints : sourcePoints, clusteredPoints.length > 0);
}

function addOverlay(data: FeatureCollection<Geometry, GeoJsonProperties>, name: string, preferredField?: string): void {
  if (data.type !== "FeatureCollection" || !Array.isArray(data.features)) throw new Error("Layer must be a GeoJSON FeatureCollection.");
  const fields = referenceFields(data);
  const field = preferredField && fields.includes(preferredField) ? preferredField : "";
  overlays.push({ id: ++overlaySeq, name, data, color: layerPalette[overlays.length % layerPalette.length], field, visible: true });
  renderOverlayList();
  layerStatus.innerHTML = `<strong>${overlays.length} reference layer${overlays.length === 1 ? "" : "s"}</strong><span>Toggle each, or colour it by a numeric field below.</span>`;
  rerenderMap();
}

function renderOverlayList(): void {
  overlayList.classList.toggle("hidden", overlays.length === 0);
  overlayList.innerHTML = overlays.map((overlay) => {
    const options = [`<option value=""${overlay.field === "" ? " selected" : ""}>Single colour</option>`,
      ...referenceFields(overlay.data).map((field) => `<option value="${field}"${overlay.field === field ? " selected" : ""}>${field.replaceAll("_", " ")}</option>`)].join("");
    return `<div class="overlay-row" data-id="${overlay.id}">
      <span class="overlay-swatch" style="background:${overlay.color}"></span>
      <div class="overlay-meta"><strong>${overlay.name}</strong><small>${overlay.data.features.length.toLocaleString()} features</small></div>
      <select class="overlay-field" data-id="${overlay.id}" aria-label="Colour by">${options}</select>
      <label class="overlay-vis"><input type="checkbox" data-id="${overlay.id}"${overlay.visible ? " checked" : ""}/><span></span></label>
      <button type="button" class="overlay-remove" data-id="${overlay.id}" aria-label="Remove layer">&times;</button>
    </div>`;
  }).join("");
}

function renderOverlays(activeMap: L.Map): void {
  overlays.forEach((overlay) => {
    overlay.layer?.remove();
    overlay.layer = undefined;
    if (!overlay.visible) return;
    const range = overlayRange(overlay);
    overlay.layer = L.geoJSON(overlay.data, {
      pointToLayer: (feature, latlng) => L.circleMarker(latlng, { radius: 4.5, weight: 0.5, color: "#fff", fillColor: colorFor(overlay, Number(feature.properties?.[overlay.field]), range), fillOpacity: overlayOpacity }),
      style: (feature) => {
        const color = colorFor(overlay, Number(feature?.properties?.[overlay.field]), range);
        return { color: overlay.field ? "#ffffff" : color, weight: overlay.field ? 0.6 : 1.2, opacity: overlayOpacity, fillColor: color, fillOpacity: overlayOpacity * 0.72 };
      },
      onEachFeature: (feature, layer) => {
        const detail = overlay.field ? `${overlay.field.replaceAll("_", " ")}: ${feature.properties?.[overlay.field] ?? "n/a"}` : "reference feature";
        layer.bindTooltip(`<strong>${overlay.name}</strong><br>${detail}`);
      },
    }).addTo(activeMap);
    overlay.layer.bringToBack();
  });
}

function renderGoogleOverlays(): void {
  if (!googleMap) return;
  const activeMap = googleMap;
  overlays.forEach((overlay) => {
    if (!overlay.visible) return;
    const range = overlayRange(overlay);
    const data = new google.maps.Data({ map: activeMap });
    data.addGeoJson(overlay.data);
    data.setStyle((feature) => {
      const color = colorFor(overlay, Number(feature.getProperty(overlay.field)), range);
      return { fillColor: color, fillOpacity: overlayOpacity * 0.72, strokeColor: overlay.field ? "#ffffff" : color, strokeOpacity: overlayOpacity, strokeWeight: overlay.field ? 0.6 : 1.2, icon: { path: google.maps.SymbolPath.CIRCLE, scale: 4, fillColor: color, fillOpacity: overlayOpacity, strokeColor: "#ffffff", strokeWeight: 0.5 } };
    });
    googleOverlayData.push(data);
  });
}
function convexHull(points: InputPoint[]): Array<[number, number]> {
  const values = points.map((item) => [item.longitude, item.latitude] as [number, number]).sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (values.length < 3) return values;
  const cross = (origin: [number, number], left: [number, number], right: [number, number]) => (left[0] - origin[0]) * (right[1] - origin[1]) - (left[1] - origin[1]) * (right[0] - origin[0]);
  const lower: Array<[number, number]> = [];
  for (const value of values) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], value) <= 0) lower.pop();
    lower.push(value);
  }
  const upper: Array<[number, number]> = [];
  for (let index = values.length - 1; index >= 0; index--) {
    const value = values[index];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], value) <= 0) upper.pop();
    upper.push(value);
  }
  lower.pop();
  upper.pop();
  return [...lower, ...upper];
}

function groupedClusters(points: ClusteredPoint[]): ClusteredPoint[][] {
  const groups = new Map<number, ClusteredPoint[]>();
  points.forEach((item) => {
    const bucket = groups.get(item.clusterId);
    if (bucket) bucket.push(item);
    else groups.set(item.clusterId, [item]);
  });
  return [...groups.values()];
}

function normalizedField(name: string): string {
  return name.trim().toLowerCase().replace(/[\s_-]+/g, "");
}

function propertyEntry(properties: Record<string, unknown>, fields: string[]): [string, unknown] | undefined {
  const wanted = new Set(fields.map(normalizedField));
  return Object.entries(properties).find(([key, value]) => wanted.has(normalizedField(key)) && String(value ?? "").trim() !== "");
}

function displayText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character]!);
}

function pointLabel(item: InputPoint): string {
  return displayText(propertyEntry(item.properties, labelFields)?.[1]) || item.id;
}

interface PointCategoryStyle {
  field: string;
  values: Array<{ value: string; color: string; count: number }>;
}

function pointCategory(item: InputPoint, style: PointCategoryStyle | undefined): string {
  if (!style) return "";
  return displayText(propertyEntry(item.properties, [style.field])?.[1]);
}

function categoryStyle(points: InputPoint[]): PointCategoryStyle | undefined {
  for (const field of categoryFields) {
    const counts = new Map<string, number>();
    for (const point of points) {
      const value = displayText(propertyEntry(point.properties, [field])?.[1]);
      if (value) counts.set(value, (counts.get(value) ?? 0) + 1);
    }
    const populated = [...counts.values()].reduce((total, count) => total + count, 0);
    const unique = counts.size;
    const maxCategories = Math.min(12, Math.max(4, Math.floor(populated * 0.4)));
    if (populated >= 2 && unique >= 2 && unique <= maxCategories) {
      return {
        field,
        values: [...counts.entries()]
          .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
          .map(([value, count], index) => ({ value, count, color: palette[index % palette.length] })),
      };
    }
  }
  return undefined;
}

function categoryColor(style: PointCategoryStyle | undefined, value: string): string | undefined {
  return style?.values.find((item) => item.value === value)?.color;
}

function markerColor(item: InputPoint, cluster: ClusteredPoint | undefined, style?: PointCategoryStyle): string {
  if (cluster) return cluster.clusterSize === 1 ? "#87939b" : palette[(cluster.clusterId - 1) % palette.length];
  return categoryColor(style, pointCategory(item, style)) ?? "#697780";
}

function propertyRows(item: InputPoint, usedKeys: Set<string>): string[] {
  return Object.entries(item.properties)
    .filter(([key, value]) => !usedKeys.has(normalizedField(key)) && !["lat", "latitude", "y", "lon", "lng", "long", "longitude", "x"].includes(normalizedField(key)) && displayText(value))
    .slice(0, 4)
    .map(([key, value]) => `<span>${escapeHtml(key.replaceAll("_", " "))}: ${escapeHtml(displayText(value))}</span>`);
}

function pointDetails(item: InputPoint, cluster: ClusteredPoint | undefined, style?: PointCategoryStyle): string {
  const label = pointLabel(item);
  const category = pointCategory(item, style);
  const usedKeys = new Set([normalizedField(propertyEntry(item.properties, labelFields)?.[0] ?? "")]);
  if (category && style) usedKeys.add(normalizedField(propertyEntry(item.properties, [style.field])?.[0] ?? ""));
  const rows = cluster
    ? [`<span>Cluster ${cluster.clusterId} - ${cluster.clusterSize} point${cluster.clusterSize === 1 ? "" : "s"}</span>`]
    : [category && category !== label ? `<span>${escapeHtml(style!.field.replaceAll("_", " "))}: ${escapeHtml(category)}</span>` : ""].filter(Boolean);
  const details = [...rows, ...propertyRows(item, usedKeys)];
  return `<strong>${escapeHtml(label)}</strong>${details.length ? `<br>${details.join("<br>")}` : ""}`;
}

function renderLeaflet(points: InputPoint[] | ClusteredPoint[], clustered: boolean, style?: PointCategoryStyle): void {
  const activeMap = ensureLeaflet();
  const bounds = L.latLngBounds([]);
  const clusters = clustered ? groupedClusters(points as ClusteredPoint[]) : [];
  renderOverlays(activeMap);

  if (clustered && hullToggle.checked) {
    clusters.filter((group) => group.length >= 3).forEach((group) => {
      const hull = convexHull(group);
      const color = palette[(group[0].clusterId - 1) % palette.length];
      L.polygon(hull.map(([longitude, latitude]) => [latitude, longitude]), { color, weight: 2, opacity: 0.7, fillColor: color, fillOpacity: 0.12, interactive: false }).addTo(hullLayer!);
    });
  }

  points.forEach((item) => {
    bounds.extend([item.latitude, item.longitude]);
    const cluster = clustered ? item as ClusteredPoint : undefined;
    const color = markerColor(item, cluster, style);
    const marker = L.circleMarker([item.latitude, item.longitude], { radius: clustered && cluster!.clusterSize > 1 ? 5.5 : 4.5, weight: 1.5, color, fillColor: color, fillOpacity: clustered ? 0.84 : 0.72 });
    if (cluster) marker.bindTooltip(pointDetails(item, cluster, style));
    else {
      const label = escapeHtml(pointLabel(item));
      marker.bindTooltip(label, { direction: "top", offset: [0, -7], opacity: 0.92, className: "point-label" });
      marker.bindPopup(pointDetails(item, undefined, style));
    }
    marker.addTo(pointLayer!);
  });
  if (bounds.isValid()) activeMap.fitBounds(bounds.pad(0.12), { maxZoom: 15 });
  setTimeout(() => activeMap.invalidateSize(), 0);
}

function loadGoogle(): Promise<void> {
  if (window.google?.maps) return Promise.resolve();
  if (googleLoader) return googleLoader;
  const key = localStorage.getItem("pointfold.googleApiKey") ?? "";
  if (!key) return Promise.reject(new Error("Add a restricted Google Maps JavaScript API key first."));
  googleLoader = new Promise((resolve, reject) => {
    const callback = `pointfoldGoogleReady_${Date.now()}`;
    (window as unknown as Record<string, unknown>)[callback] = () => {
      delete (window as unknown as Record<string, unknown>)[callback];
      resolve();
    };
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&loading=async&callback=${callback}&v=weekly`;
    script.async = true;
    script.onerror = () => reject(new Error("Google Maps could not load. Check the key, billing, API restrictions, and referrer restrictions."));
    document.head.appendChild(script);
  });
  return googleLoader;
}

async function renderGoogle(points: InputPoint[] | ClusteredPoint[], clustered: boolean, style?: PointCategoryStyle): Promise<void> {
  await loadGoogle();
  destroyLeaflet();
  clearGoogleOverlays();
  const mapId = localStorage.getItem("pointfold.googleMapId") || undefined;
  const center = points.length ? { lat: points[0].latitude, lng: points[0].longitude } : { lat: 43.6532, lng: -79.3832 };
  googleMap = new google.maps.Map(byId<HTMLElement>("map"), { center, zoom: 10, mapId, mapTypeControl: false, streetViewControl: false, fullscreenControl: false, gestureHandling: "greedy", mapTypeId: "roadmap" });
  const bounds = new google.maps.LatLngBounds();
  const clusters = clustered ? groupedClusters(points as ClusteredPoint[]) : [];
  renderGoogleOverlays();

  if (clustered && hullToggle.checked) {
    clusters.filter((group) => group.length >= 3).forEach((group) => {
      const color = palette[(group[0].clusterId - 1) % palette.length];
      const polygon = new google.maps.Polygon({ paths: convexHull(group).map(([lng, lat]) => ({ lat, lng })), strokeColor: color, strokeOpacity: 0.75, strokeWeight: 2, fillColor: color, fillOpacity: 0.12, map: googleMap });
      googleOverlays.push(polygon);
    });
  }

  points.forEach((item) => {
    bounds.extend({ lat: item.latitude, lng: item.longitude });
    const cluster = clustered ? item as ClusteredPoint : undefined;
    const color = markerColor(item, cluster, style);
    const circle = new google.maps.Circle({ center: { lat: item.latitude, lng: item.longitude }, radius: clustered && cluster!.clusterSize > 1 ? 55 : 35, strokeColor: color, strokeOpacity: 1, strokeWeight: 1.5, fillColor: color, fillOpacity: clustered ? 0.84 : 0.72, map: googleMap });
    const info = new google.maps.InfoWindow({ content: pointDetails(item, cluster, style) });
    circle.addListener("click", () => {
      info.setPosition({ lat: item.latitude, lng: item.longitude });
      info.open({ map: googleMap });
    });
    googleOverlays.push(circle);
  });
  if (!bounds.isEmpty()) googleMap.fitBounds(bounds, 42);
}

async function renderCesium(points: InputPoint[] | ClusteredPoint[], clustered: boolean, style?: PointCategoryStyle): Promise<void> {
  const { Cartesian3, Cesium3DTileset, Color, GeoJsonDataSource, Ion, Viewer, createOsmBuildingsAsync } = await import("cesium");
  const token = localStorage.getItem("pointfold.cesiumToken") ?? "";
  if (!token) throw new Error("Add a restricted Cesium ion access token first.");
  Ion.defaultAccessToken = token;
  destroyLeaflet();
  clearGoogleOverlays();
  googleMap = undefined;
  destroyCesium();
  document.querySelector(".map-stage")?.classList.add("cesium-active");
  cesiumViewer = new Viewer("map", { animation: false, timeline: false, sceneModePicker: false, baseLayerPicker: true, geocoder: false, navigationHelpButton: false });

  overlays.filter((overlay) => overlay.visible).forEach((overlay) => {
    const color = Color.fromCssColorString(overlay.color);
    void GeoJsonDataSource.load(overlay.data, { clampToGround: true, stroke: color.withAlpha(overlayOpacity), fill: color.withAlpha(overlayOpacity * 0.55), strokeWidth: 2, markerColor: color, markerSize: 8 })
      .then((dataSource) => cesiumViewer?.dataSources.add(dataSource));
  });
  const clusters = clustered ? groupedClusters(points as ClusteredPoint[]) : [];
  if (clustered && hullToggle.checked) {
    clusters.filter((group) => group.length >= 3).forEach((group) => {
      const color = Color.fromCssColorString(palette[(group[0].clusterId - 1) % palette.length]);
      cesiumViewer!.entities.add({ polygon: { hierarchy: Cartesian3.fromDegreesArray(convexHull(group).flat()), material: color.withAlpha(0.18), outline: true, outlineColor: color } });
    });
  }
  points.forEach((item) => {
    const cluster = clustered ? item as ClusteredPoint : undefined;
    const css = markerColor(item, cluster, style);
    cesiumViewer!.entities.add({
      name: cluster ? `Cluster ${cluster.clusterId} - ${pointLabel(item)}` : pointLabel(item),
      position: Cartesian3.fromDegrees(item.longitude, item.latitude, 8),
      point: { pixelSize: clustered && cluster!.clusterSize > 1 ? 10 : 7, color: Color.fromCssColorString(css), outlineColor: Color.WHITE, outlineWidth: 1 },
    });
  });
  const tileErrors: string[] = [];
  const optionalTilesets: Array<{ label: string; load: () => ReturnType<typeof createOsmBuildingsAsync> | ReturnType<typeof Cesium3DTileset.fromIonAssetId> }> = [];
  if (localStorage.getItem("pointfold.cesiumOsmBuildings") !== "false") {
    optionalTilesets.push({ label: "OSM Buildings asset 96188", load: () => createOsmBuildingsAsync() });
  }
  const assetIds = (localStorage.getItem("pointfold.cesiumAssets") ?? "").split(",").map((value) => Number(value.trim())).filter((value) => Number.isInteger(value) && value > 0);
  assetIds.forEach((assetId) => {
    optionalTilesets.push({ label: `ion asset ${assetId}`, load: () => Cesium3DTileset.fromIonAssetId(assetId) });
  });
  for (const tileset of optionalTilesets) {
    try {
      cesiumViewer.scene.primitives.add(await tileset.load());
    } catch (error) {
      console.error(`Could not load Cesium ${tileset.label}`, error);
      tileErrors.push(tileset.label);
    }
  }
  if (points.length) await cesiumViewer.zoomTo(cesiumViewer.entities.values);
  if (tileErrors.length) {
    setStatus(`3D opened, but Cesium could not load ${tileErrors.join(", ")}. Check that the token is scoped to those ion assets, or uncheck OSM Buildings.`, true);
  }
}

function updateLegend(points: ClusteredPoint[]): void {
  const legend = byId("cluster-legend");
  if (!points.length) { legend.classList.add("hidden"); return; }
  const groups = groupedClusters(points).sort((a, b) => b.length - a.length).slice(0, 6);
  legend.innerHTML = `<div class="legend-title"><span>Largest clusters</span><b>${linkageCopy[linkage].title}</b></div>${groups.map((group) => `<div><i style="background:${group[0].clusterSize === 1 ? "#87939b" : palette[(group[0].clusterId - 1) % palette.length]}"></i><span>Cluster ${group[0].clusterId}</span><strong>${group.length}</strong></div>`).join("")}`;
  legend.classList.remove("hidden");
}

function updateCategoryLegend(style: PointCategoryStyle | undefined): void {
  const legend = byId("cluster-legend");
  if (!style) { legend.classList.add("hidden"); return; }
  legend.innerHTML = `<div class="legend-title"><span>Point categories</span><b>${escapeHtml(style.field.replaceAll("_", " "))}</b></div>${style.values.slice(0, 8).map((item) => `<div><i style="background:${item.color}"></i><span>${escapeHtml(item.value)}</span><strong>${item.count}</strong></div>`).join("")}`;
  legend.classList.remove("hidden");
}

function renderMap(points: InputPoint[] | ClusteredPoint[], clustered: boolean): void {
  emptyState.classList.add("hidden");
  results.classList.remove("hidden");
  const pointStyle = clustered ? undefined : categoryStyle(points as InputPoint[]);
  if (mapStyle === "google") {
    void renderGoogle(points, clustered, pointStyle).catch((error) => {
      setStatus(error instanceof Error ? error.message : "Google Maps could not load.", true);
      googleApiKey.value = localStorage.getItem("pointfold.googleApiKey") ?? "";
      googleMapId.value = localStorage.getItem("pointfold.googleMapId") ?? "";
      googleDialog.showModal();
      mapStyle = "light";
      updateMapStyleButtons();
      renderLeaflet(points, clustered, pointStyle);
    });
  } else if (mapStyle === "cesium") {
    void renderCesium(points, clustered, pointStyle).catch((error) => {
      setStatus(error instanceof Error ? error.message : "Cesium could not load.", true);
      cesiumToken.value = localStorage.getItem("pointfold.cesiumToken") ?? "";
      cesiumAssets.value = localStorage.getItem("pointfold.cesiumAssets") ?? "";
      cesiumOsmBuildings.checked = localStorage.getItem("pointfold.cesiumOsmBuildings") !== "false";
      if (!cesiumDialog.open) cesiumDialog.showModal();
      mapStyle = "light";
      updateMapStyleButtons();
      renderLeaflet(points, clustered, pointStyle);
    });
  } else renderLeaflet(points, clustered, pointStyle);

  byId("point-count").textContent = points.length.toLocaleString();
  byId("dataset-name").textContent = currentName;
  if (!clustered) {
    byId("cluster-count").textContent = "-";
    byId("singleton-count").textContent = "-";
    byId("runtime").textContent = "-";
    updateCategoryLegend(pointStyle);
  }
}

function renderClusters(points: ClusteredPoint[], elapsed: number): void {
  renderMap(points, true);
  const clusterIds = new Set(points.map((item) => item.clusterId));
  const singletons = points.filter((item) => item.clusterSize === 1).length;
  byId("cluster-count").textContent = clusterIds.size.toLocaleString();
  byId("singleton-count").textContent = singletons.toLocaleString();
  byId("runtime").textContent = elapsed < 1 ? "<1 ms" : `${Math.round(elapsed)} ms`;
  byId("method-footer").textContent = `${linkageCopy[linkage].title} � Haversine � WGS84`;
  updateLegend(points);
}

function updateMapStyleButtons(): void {
  document.querySelectorAll<HTMLButtonElement>("[data-map-style]").forEach((button) => button.classList.toggle("active", button.dataset.mapStyle === mapStyle));
}

async function handleFile(file: File): Promise<void> {
  setStatus(`Reading ${file.name}...`);
  try { loadPoints(await parseFile(file), file.name); }
  catch (error) { setStatus(error instanceof Error ? error.message : "Could not read this file.", true); }
}

fileInput.addEventListener("change", () => { const file = fileInput.files?.[0]; if (file) void handleFile(file); });
["dragenter", "dragover"].forEach((name) => dropzone.addEventListener(name, (event) => { event.preventDefault(); dropzone.classList.add("dragging"); }));
["dragleave", "drop"].forEach((name) => dropzone.addEventListener(name, (event) => { event.preventDefault(); dropzone.classList.remove("dragging"); }));
dropzone.addEventListener("drop", (event) => { const file = event.dataTransfer?.files[0]; if (file) void handleFile(file); });

document.querySelectorAll<HTMLButtonElement>("[data-demo]").forEach((button) => button.addEventListener("click", () => {
  document.querySelectorAll("[data-demo]").forEach((item) => item.classList.remove("active"));
  button.classList.add("active");
  const demo = demoData(button.dataset.demo ?? "neighborhoods");
  units.value = "kilometers";
  loadPoints(demo.points, demo.label, demo.threshold);
}));

document.querySelectorAll<HTMLButtonElement>("[data-linkage]").forEach((button) => button.addEventListener("click", () => {
  linkage = button.dataset.linkage as Linkage;
  document.querySelectorAll<HTMLButtonElement>("[data-linkage]").forEach((item) => { const active = item === button; item.classList.toggle("active", active); item.setAttribute("aria-checked", String(active)); });
  byId("hero-method").textContent = linkageCopy[linkage].title;
  byId("linkage-explainer").innerHTML = `<b>${linkageCopy[linkage].title}</b> ${linkageCopy[linkage].description}`;
  if (sourcePoints.length > 1200 && linkage !== "single") setStatus(`${linkageCopy[linkage].title} is exact and limited to 1,200 points. Choose Single-link for this file.`, true);
  else if (sourcePoints.length) setStatus(`${linkageCopy[linkage].title} selected. Ready to build clusters.`);
}));

slider.addEventListener("input", () => { thresholdInput.value = slider.value; });
thresholdInput.addEventListener("input", () => { const value = Number(thresholdInput.value); if (value > 0 && value <= Number(slider.max)) slider.value = String(value); });

clusterButton.addEventListener("click", () => {
  clusterButton.disabled = true;
  clusterButton.classList.add("working");
  setStatus(`Building ${linkageCopy[linkage].title.toLowerCase()} clusters...`);
  requestAnimationFrame(() => setTimeout(() => {
    try {
      const start = performance.now();
      clusteredPoints = clusterPoints(sourcePoints, Number(thresholdInput.value), units.value as DistanceUnit, linkage);
      const elapsed = performance.now() - start;
      renderClusters(clusteredPoints, elapsed);
      setStatus(`Complete. ${new Set(clusteredPoints.map((item) => item.clusterId)).size} clusters found with ${linkageCopy[linkage].title}.`);
    } catch (error) { setStatus(error instanceof Error ? error.message : "Clustering failed.", true); }
    finally { clusterButton.disabled = false; clusterButton.classList.remove("working"); }
  }, 20));
});

document.querySelectorAll<HTMLButtonElement>("[data-map-style]").forEach((button) => button.addEventListener("click", () => {
  mapStyle = button.dataset.mapStyle ?? "light";
  updateMapStyleButtons();
  if (sourcePoints.length) renderMap(clusteredPoints.length ? clusteredPoints : sourcePoints, clusteredPoints.length > 0);
}));

hullToggle.addEventListener("change", () => { if (sourcePoints.length) renderMap(clusteredPoints.length ? clusteredPoints : sourcePoints, clusteredPoints.length > 0); });
byId("layer-settings").addEventListener("click", () => layerDialog.showModal());
byId("load-onmarg").addEventListener("click", async () => {
  layerStatus.innerHTML = "<strong>Loading ON-Marg...</strong><span>Preparing the Greater Toronto reference sample.</span>";
  try {
    const response = await fetch("/layers/onmarg-gta-2021.geojson");
    if (!response.ok) throw new Error(`Layer request failed (${response.status}).`);
    addOverlay(await response.json() as FeatureCollection<Geometry, GeoJsonProperties>, "2021 ON-Marg � Greater Toronto sample");
  } catch (error) { setStatus(error instanceof Error ? error.message : "Could not load ON-Marg.", true); }
});
byId<HTMLInputElement>("layer-file").addEventListener("change", async (event) => {
  const input = event.currentTarget as HTMLInputElement;
  for (const file of Array.from(input.files ?? [])) {
    try {
      if (file.name.toLowerCase().endsWith(".csv")) {
        const points = await parseFile(file);
        addOverlay({
          type: "FeatureCollection",
          features: points.map((point) => ({ type: "Feature", geometry: { type: "Point", coordinates: [point.longitude, point.latitude] }, properties: { id: point.id, ...point.properties } })),
        }, file.name);
      } else addOverlay(JSON.parse(await file.text()) as FeatureCollection<Geometry, GeoJsonProperties>, file.name);
    } catch (error) { setStatus(`${file.name}: ${error instanceof Error ? error.message : "Could not read this layer."}`, true); }
  }
  input.value = "";
});
overlayList.addEventListener("change", (event) => {
  const input = event.target as HTMLInputElement | HTMLSelectElement;
  const overlay = overlays.find((item) => item.id === Number(input.dataset.id));
  if (!overlay) return;
  if (input.classList.contains("overlay-field")) overlay.field = input.value;
  else overlay.visible = (input as HTMLInputElement).checked;
  rerenderMap();
});
overlayList.addEventListener("click", (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>(".overlay-remove");
  if (!button) return;
  overlays = overlays.filter((item) => item.id !== Number(button.dataset.id));
  renderOverlayList();
  layerStatus.innerHTML = overlays.length ? `<strong>${overlays.length} reference layers</strong><span>Toggle or style each layer independently.</span>` : "<strong>No reference layer</strong><span>Your uploaded layers stay in this browser.</span>";
  rerenderMap();
});
layerOpacity.addEventListener("input", () => { overlayOpacity = Number(layerOpacity.value); rerenderMap(); });
byId("cesium-settings").addEventListener("click", () => {
  cesiumToken.value = localStorage.getItem("pointfold.cesiumToken") ?? "";
  cesiumAssets.value = localStorage.getItem("pointfold.cesiumAssets") ?? "";
  cesiumOsmBuildings.checked = localStorage.getItem("pointfold.cesiumOsmBuildings") !== "false";
  cesiumDialog.showModal();
});
byId("save-cesium-key").addEventListener("click", () => {
  if (!cesiumToken.value.trim()) { setStatus("Enter a Cesium ion access token.", true); return; }
  localStorage.setItem("pointfold.cesiumToken", cesiumToken.value.trim());
  localStorage.setItem("pointfold.cesiumAssets", cesiumAssets.value.trim());
  localStorage.setItem("pointfold.cesiumOsmBuildings", String(cesiumOsmBuildings.checked));
  cesiumDialog.close();
  mapStyle = "cesium";
  updateMapStyleButtons();
  rerenderMap();
});
byId("clear-cesium-key").addEventListener("click", () => {
  ["pointfold.cesiumToken", "pointfold.cesiumAssets", "pointfold.cesiumOsmBuildings"].forEach((key) => localStorage.removeItem(key));
  cesiumToken.value = "";
  cesiumAssets.value = "";
  setStatus("Saved Cesium settings cleared.");
});
byId("google-settings").addEventListener("click", () => {
  googleApiKey.value = localStorage.getItem("pointfold.googleApiKey") ?? "";
  googleMapId.value = localStorage.getItem("pointfold.googleMapId") ?? "";
  googleDialog.showModal();
});
byId("save-google-key").addEventListener("click", () => {
  const key = googleApiKey.value.trim();
  if (!key) { setStatus("Enter a Google Maps JavaScript API key.", true); return; }
  localStorage.setItem("pointfold.googleApiKey", key);
  localStorage.setItem("pointfold.googleMapId", googleMapId.value.trim());
  googleDialog.close();
  setStatus("Google Maps key saved in this browser. Use Google or Live traffic from the map toolbar.");
});
byId("clear-google-key").addEventListener("click", () => {
  localStorage.removeItem("pointfold.googleApiKey");
  localStorage.removeItem("pointfold.googleMapId");
  googleApiKey.value = "";
  googleMapId.value = "";
  setStatus("Saved Google Maps configuration cleared.");
});

function download(content: string, filename: string, type: string): void {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function exportPoints(): Array<InputPoint | ClusteredPoint> {
  return clusteredPoints.length ? clusteredPoints : sourcePoints;
}

function exportedGeoJson(): FeatureCollection<Geometry, GeoJsonProperties> {
  return { type: "FeatureCollection", features: exportPoints().map((item) => ({ type: "Feature", id: item.id, geometry: { type: "Point", coordinates: [item.longitude, item.latitude] }, properties: { ...item.properties, id: item.id, ...("clusterId" in item ? { cluster_id: item.clusterId, cluster_size: item.clusterSize, linkage } : {}) } })) };
}
byId("export-csv").addEventListener("click", () => {
  const points = exportPoints();
  if (!points.length) { setStatus("Load a dataset before exporting CSV.", true); return; }
  const clustered = clusteredPoints.length > 0;
  const escape = (value: unknown) => `"${String(value ?? "").replaceAll('"', '""')}"`;
  const headers = clustered ? ["id", "latitude", "longitude", "cluster_id", "cluster_size", "linkage"] : ["id", "latitude", "longitude"];
  const rows = [headers.join(","), ...points.map((item) => {
    const cluster = clustered ? item as ClusteredPoint : undefined;
    return [item.id, item.latitude, item.longitude, ...(cluster ? [cluster.clusterId, cluster.clusterSize, linkage] : [])].map(escape).join(",");
  })];
  download(`\ufeff${rows.join("\r\n")}`, clustered ? "pointfold-clusters.csv" : "pointfold-points.csv", "text/csv;charset=utf-8");
  setStatus(`Exported ${points.length.toLocaleString()} points as CSV.`);
});
byId("export-geojson").addEventListener("click", () => {
  if (!sourcePoints.length) { setStatus("Load a dataset before exporting GeoJSON.", true); return; }
  download(JSON.stringify(exportedGeoJson(), null, 2), clusteredPoints.length ? "pointfold-clusters.geojson" : "pointfold-points.geojson", "application/geo+json;charset=utf-8");
  setStatus(`Exported ${exportPoints().length.toLocaleString()} points as GeoJSON.`);
});
byId("export-html").addEventListener("click", () => {
  if (!sourcePoints.length) { setStatus("Load a dataset before exporting HTML.", true); return; }
  const payload = JSON.stringify({ name: currentName, points: exportedGeoJson(), overlays: overlays.filter((overlay) => overlay.visible).map(({ name, data, color }) => ({ name, data, color })) }).replaceAll("</script", "<\\/script");
  const title = currentName.replaceAll("&", "&amp;").replaceAll("<", "&lt;");
  const html = `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width"><title>${title} - Pointfold</title><link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"><style>html,body,#map{height:100%;margin:0}.title{position:fixed;z-index:1000;top:12px;left:50%;transform:translateX(-50%);background:#fffffff0;padding:10px 14px;border-radius:6px;font:14px system-ui;box-shadow:0 4px 20px #0003}</style></head><body><div class="title"><b>${title}</b> - ${exportPoints().length.toLocaleString()} points</div><div id="map"></div><script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script><script>const p=${payload};const m=L.map('map');L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',{attribution:'OpenStreetMap contributors'}).addTo(m);const a=[];p.overlays.forEach(o=>a.push(L.geoJSON(o.data,{style:{color:o.color,fillColor:o.color,fillOpacity:.3},pointToLayer:(_,x)=>L.circleMarker(x,{radius:4,color:o.color,fillOpacity:.8})}).addTo(m)));a.push(L.geoJSON(p.points,{pointToLayer:(f,x)=>L.circleMarker(x,{radius:f.properties.cluster_size>1?6:4,color:'#fff',weight:1,fillColor:f.properties.cluster_id?'#f05a3d':'#697780',fillOpacity:.85})}).addTo(m));const g=L.featureGroup(a);if(g.getBounds().isValid())m.fitBounds(g.getBounds().pad(.08));<\/script></body></html>`;
  download(html, "pointfold-map.html", "text/html;charset=utf-8");
  setStatus("Exported an HTML map. It loads Leaflet and map tiles from the internet when opened.");
});window.addEventListener("beforeprint", () => leafletMap?.invalidateSize());
byId("export-pdf").addEventListener("click", () => {
  if (!sourcePoints.length) { setStatus("Load and map some points before saving a PDF.", true); return; }
  if (mapStyle === "google") setStatus("Tip: switch off the Google basemap before printing � it may not appear in the PDF.");
  leafletMap?.invalidateSize();
  setTimeout(() => window.print(), 250);
});



